/**
 * Letter composer Alpine.js component.
 *
 * Left pane is the letter form; right pane is a live A4 preview. The preview
 * is the REAL printable document: the form posts its current fields to
 * letters#preview, which renders the same HTML the PDF Lambda prints
 * (letterhead, fonts, pagination script and all), and the response becomes
 * the iframe's srcdoc. What you see is exactly what prints.
 */
export function letterComposer(config) {
  return {
    recipientQuery: "",
    recipientResults: [],
    recipientOpen: false,
    scale: 1,
    frameHeight: 1123,
    previewTimer: null,
    aiOpen: false,
    aiInstruction: "",
    aiDrafting: false,
    aiError: null,

    init() {
      this.$refs.form.addEventListener("input", () => this.schedulePreview())
      this.$refs.form.addEventListener("change", () => this.schedulePreview())
      // The page mounts inside an x-cloak'd container, so the pane can
      // measure 0 wide during Alpine init — defer the first measure and
      // re-measure whenever the pane's size actually changes.
      requestAnimationFrame(() => this.computeScale())
      this._resizeObserver = new ResizeObserver(() => this.computeScale())
      this._resizeObserver.observe(this.$refs.pane)
      this.refreshPreview()
    },

    computeScale() {
      const available = this.$refs.pane.clientWidth - 48
      if (available < 100) return // hidden or mid-layout; observer will re-fire
      this.scale = Math.min(1, Math.max(0.35, available / 794))
    },

    schedulePreview() {
      clearTimeout(this.previewTimer)
      this.previewTimer = setTimeout(() => this.refreshPreview(), 500)
    },

    async refreshPreview() {
      const body = new FormData(this.$refs.form)
      // The edit form carries Rails' _method=patch override; forwarding it
      // would turn our POST into PATCH /letters/preview, which doesn't route.
      body.delete("_method")
      try {
        const resp = await fetch(config.previewUrl, {
          method: "POST",
          headers: {
            "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content,
            "Accept": "text/html"
          },
          body
        })
        if (!resp.ok) return
        this.$refs.frame.srcdoc = await resp.text()
      } catch {
        // Preview is best-effort; the next keystroke retries.
      }
    },

    frameLoaded() {
      // The preview document paginates itself after its fonts settle, so
      // measure once now and again after the pagination pass has run.
      const measure = () => {
        const doc = this.$refs.frame.contentDocument
        if (!doc?.body) return
        // Measure the BODY, not documentElement — the latter is floored at
        // the iframe's viewport height, so the frame could grow to two
        // pages but never shrink back to one. Floor at one A4 page.
        this.frameHeight = Math.max(1123, Math.ceil(doc.body.getBoundingClientRect().height))
      }
      measure()
      setTimeout(measure, 350)
      setTimeout(measure, 900)
    },

    async searchRecipients() {
      const q = this.recipientQuery.trim()
      if (q.length < 2) {
        this.recipientResults = []
        this.recipientOpen = false
        return
      }
      const params = new URLSearchParams({ q })
      for (const type of [ "landlord", "person", "contractor" ]) params.append("types[]", type)
      try {
        const resp = await fetch(`/api/people/search?${params}`, { headers: { "Accept": "application/json" } })
        if (!resp.ok) return
        const results = await resp.json()
        this.recipientResults = (Array.isArray(results) ? results : []).slice(0, 8)
        this.recipientOpen = true
      } catch {
        // Ignore — user keeps typing, next search retries.
      }
    },

    // "Help me write": sends the instruction plus the current form state to
    // the server, which grounds the draft in the recipient's tenancy records.
    async draftWithAi() {
      const instruction = this.aiInstruction.trim()
      if (!instruction) {
        this.aiError = "Say what the letter should cover."
        return
      }
      this.aiDrafting = true
      this.aiError = null
      const form = new FormData(this.$refs.form)
      try {
        const resp = await fetch(config.helpMeWriteUrl, {
          method: "POST",
          headers: {
            "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content,
            "Content-Type": "application/json",
            "Accept": "application/json"
          },
          body: JSON.stringify({
            instruction,
            recipient_type: form.get("letter[recipient_type]"),
            recipient_id: form.get("letter[recipient_id]"),
            subject: form.get("letter[subject]"),
            body: form.get("letter[body]"),
            addressee_text: form.get("letter[addressee_text]"),
            signatory_name: form.get("letter[signatory_name]"),
            signatory_role: form.get("letter[signatory_role]")
          })
        })
        const json = await resp.json()
        if (json.success) {
          if (json.subject) this.$refs.subject.value = json.subject
          this.$refs.body.value = json.body
          this.aiOpen = false
          this.aiInstruction = ""
          this.refreshPreview()
        } else {
          this.aiError = json.error || "Drafting failed — try again."
        }
      } catch {
        this.aiError = "Drafting failed — try again."
      } finally {
        this.aiDrafting = false
      }
    },

    async selectRecipient(result) {
      this.recipientOpen = false
      this.recipientQuery = result.name
      // Search ids look like "landlord_123" — split into the polymorphic pair.
      this.$refs.recipientType.value = result.type
      this.$refs.recipientId.value = String(result.id).split("_").pop()

      const params = new URLSearchParams({
        recipient_type: result.type,
        recipient_id: this.$refs.recipientId.value
      })
      try {
        const resp = await fetch(`${config.addresseeUrl}?${params}`, { headers: { "Accept": "application/json" } })
        if (resp.ok) {
          const json = await resp.json()
          if (json.lines?.length) this.$refs.addressee.value = json.lines.join("\n")
        }
      } catch {
        // Address stays as typed.
      }
      this.schedulePreview()
    }
  }
}
